import { lazy, Suspense, useEffect } from 'react'
import { Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { AutoLoginRoute, PrivateRoute, PublicOnlyRoute } from '../features/auth'
import NotFoundPage from '../components/errors/NotFoundPage'
import { ROUTES } from '../constants/routes'
import { LandingPage } from '../features/landing'
import { HomePage } from '../features/home'
import AuthRouteFallback from '../features/auth/components/AuthRouteFallback'
import SignUpFlowRoute from '../features/auth/components/SignUpFlowRoute'
import SignInPage from '../features/auth/pages/SignInPage'
import LegalRouteFallback from '../features/legal/components/LegalRouteFallback'
import LegalOnboardingRoute from '../features/legal/components/LegalOnboardingRoute'
import AppShell from './layouts/AppShell'
import { CORE_LEGAL_REQUIREMENT_EVENT } from '../constants/events'

const SignUpPage = lazy(() => import('../features/auth/pages/SignUpPage'))
const SignUpConsentPage = lazy(() => import('../features/auth/pages/SignUpConsentPage'))
const EmailAuthPage = lazy(() => import('../features/auth/pages/EmailAuthPage'))
const PasswordResetPage = lazy(() => import('../features/auth/pages/PasswordResetPage'))
const MembershipConsentPage = lazy(() => import('../features/auth/pages/MembershipConsentPage'))
const ProfileLayout = lazy(() => import('../features/profile/pages/ProfileLayout'))
const ProfileEditPage = lazy(() => import('../features/profile/pages/ProfileEditPage'))
const PrivacyConsentPage = lazy(() => import('../features/profile/pages/PrivacyConsentPage'))
const PlannerLayout = lazy(() => import('../features/planner/pages/PlannerLayout'))
const SessionalPlanPage = lazy(() => import('../features/planner/pages/SessionalPlanPage'))
const WeeklyPlanPage = lazy(() => import('../features/planner/pages/WeeklyPlanPage'))
const ReportLayout = lazy(() => import('../features/reports/pages/ReportLayout'))
const ReportListPage = lazy(() => import('../features/reports/pages/ReportListPage'))
const ReportDetailPage = lazy(() => import('../features/reports/pages/ReportDetailPage'))
const ReportCreatePage = lazy(() => import('../features/reports/pages/ReportCreatePage'))
const ReportEditPage = lazy(() => import('../features/reports/pages/ReportEditPage'))
const DailyLogPage = lazy(() => import('../features/daily-log/pages/DailyLogPage'))
const RoutineGoalsPage = lazy(() => import('../features/routine-goals/pages/RoutineGoalsPage'))
const TermsOfServicePage = lazy(() => import('../features/legal/pages/TermsOfServicePage'))
const TermsOfServiceHistoryPage = lazy(() => import('../features/legal/pages/TermsOfServiceHistoryPage'))
const OpenSourceLicensesPage = lazy(() => import('../features/legal/pages/OpenSourceLicensesPage'))
const PrivacyPolicyPage = lazy(() => import('../features/legal/pages/PrivacyPolicyPage'))
const PrivacyPolicyHistoryPage = lazy(() => import('../features/legal/pages/PrivacyPolicyHistoryPage'))

function deferred(element, fallback) {
  return <Suspense fallback={fallback}>{element}</Suspense>
}
export default function AppRoutes() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const handleCoreLegalRequirement = () => {
      if (location.pathname === ROUTES.POLICY_CONSENT) return
      navigate(ROUTES.POLICY_CONSENT, { replace: true })
    }
    window.addEventListener(CORE_LEGAL_REQUIREMENT_EVENT, handleCoreLegalRequirement)
    return () => window.removeEventListener(
      CORE_LEGAL_REQUIREMENT_EVENT,
      handleCoreLegalRequirement,
    )
  }, [location.pathname, navigate])

  return (
    <Routes>
      <Route path="/" element={<AutoLoginRoute element={<LandingPage />} />} />
      <Route path={ROUTES.ABOUT} element={<LandingPage />} />
      <Route
        path={ROUTES.TERMS_OF_SERVICE}
        element={deferred(<TermsOfServicePage />, <LegalRouteFallback variant="terms" />)}
      />
      <Route
        path={ROUTES.TERMS_OF_SERVICE_HISTORY}
        element={deferred(<TermsOfServiceHistoryPage />, <LegalRouteFallback variant="terms" />)}
      />
      <Route
        path={`${ROUTES.TERMS_OF_SERVICE_HISTORY}/:legalDocumentId`}
        element={deferred(<TermsOfServicePage />, <LegalRouteFallback variant="terms" />)}
      />
      <Route
        path={ROUTES.OPEN_SOURCE_LICENSES}
        element={deferred(<OpenSourceLicensesPage />, <LegalRouteFallback variant="licenses" />)}
      />
      <Route
        path={ROUTES.PRIVACY_POLICY}
        element={deferred(<PrivacyPolicyPage />, <LegalRouteFallback variant="privacy" />)}
      />
      <Route
        path={ROUTES.PRIVACY_POLICY_HISTORY}
        element={deferred(<PrivacyPolicyHistoryPage />, <LegalRouteFallback variant="privacy" />)}
      />
      <Route
        path={`${ROUTES.PRIVACY_POLICY_HISTORY}/:legalDocumentId`}
        element={deferred(<PrivacyPolicyPage />, <LegalRouteFallback variant="privacy" />)}
      />
      <Route
        path={ROUTES.POLICY_CONSENT}
        element={deferred(
          <PrivateRoute element={<MembershipConsentPage />} />,
          <AuthRouteFallback variant="consent" />,
        )}
      />
      <Route element={<PrivateRoute element={<AppShell />} />}>
        <Route element={<LegalOnboardingRoute element={<Outlet />} />}>
          <Route path={ROUTES.HOME} element={<HomePage />} />
          <Route path={ROUTES.MY_PAGE} element={<ProfileLayout />}>
            <Route index element={<ProfileEditPage />} />
            <Route path="privacy" element={<PrivacyConsentPage />} />
          </Route>
          <Route path={ROUTES.PLANS} element={<PlannerLayout />}>
            <Route index element={<SessionalPlanPage />} />
            <Route path=":sessionalPlanId/weeks" element={<WeeklyPlanPage />} />
          </Route>
          <Route path={ROUTES.REPORTS} element={<ReportLayout />}>
            <Route index element={<ReportListPage />} />
            <Route path="new" element={<ReportCreatePage />} />
            <Route path=":reportId" element={<ReportDetailPage />} />
            <Route path=":reportId/edit" element={<ReportEditPage />} />
          </Route>
          <Route path={ROUTES.DAILY_LOGS} element={<DailyLogPage />} />
          <Route path={ROUTES.ROUTINE_GOALS} element={<RoutineGoalsPage />} />
        </Route>
      </Route>
      <Route path={ROUTES.LOGIN} element={<PublicOnlyRoute element={<SignInPage />} />} />
      <Route
        path={ROUTES.SIGN_UP}
        element={deferred(
          <PublicOnlyRoute element={<SignUpConsentPage />} />,
          <AuthRouteFallback variant="sign-up-consent" />,
        )}
      />
      <Route
        path={ROUTES.SIGN_UP_EMAIL_VERIFICATION}
        element={deferred(
          <PublicOnlyRoute
            element={(
              <SignUpFlowRoute
                stage="email"
                element={<EmailAuthPage verificationPurpose="SIGN_UP" />}
              />
            )}
          />,
          <AuthRouteFallback variant="email" />,
        )}
      />
      <Route
        path={ROUTES.SIGN_UP_DETAILS}
        element={deferred(
          <PublicOnlyRoute
            element={(
              <SignUpFlowRoute stage="details" element={<SignUpPage />} />
            )}
          />,
          <AuthRouteFallback variant="sign-up" />,
        )}
      />
      <Route
        path={ROUTES.PASSWORD_RESET_EMAIL_VERIFICATION}
        element={deferred(
          <PublicOnlyRoute element={<EmailAuthPage verificationPurpose="PASSWORD_RESET" />} />,
          <AuthRouteFallback variant="email" />,
        )}
      />
      <Route
        path={ROUTES.PASSWORD_RESET}
        element={deferred(
          <PublicOnlyRoute element={<PasswordResetPage />} />,
          <AuthRouteFallback variant="password-reset" />,
        )}
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
